import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
  Text
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiAlertCircle, FiCheckCircle, FiClock, FiImage, FiSearch, FiSliders, FiStar, FiUsers } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'

import { ActionButton } from '../../../components/ActionButton'
import { EmptyState } from '../../../components/EmptyState'
import { FormField } from '../../../components/FormField'
import LoadingBlock from '../../../components/Loading/LoadingBlock'
import { PageTitle } from '../../../components/PageTitle'
import Paginator from '../../../components/paginator'
import { SectionCard } from '../../../components/SectionCard'
import { SearchableSelect } from '../../../components/Select'
import { Tooltip } from '../../../components/Tooltip'
import { useAppToast, useHasRole } from '../../../helpers'
import ContestEvaluationCriteriaService from '../../../services/Contests/ContestEvaluationCriteriaService'
import ContestEvaluatorsService from '../../../services/Contests/ContestEvaluatorsService'
import ContestsService from '../../../services/Contests/ContestsService'
import {
  DEFAULT_MAX_SHOWCASE_POSITION,
  IApplicantSelectOption,
  IArtworkEvaluationFilter,
  IArtworkShowcaseState,
  IShowcaseTakenPosition,
  IContestArtworkForEvaluation,
  IContestEvaluationCriterion,
  IContestEvaluator,
  IContestForSelect
} from '../../../services/Contests/interfaces'
import ArtworkEvaluationCard from './ArtworkEvaluationCard'
import ArtworkEvaluationModal from './ArtworkEvaluationModal'
import ContestSelectionGate from './ContestSelectionGate'
import CriteriaManagementModal from './CriteriaManagementModal'
import EvaluatorsManagementModal from './EvaluatorsManagementModal'

const MotionBox = motion(Box)
const MotionSectionCard = motion(SectionCard)
const MotionEmptyState = motion(EmptyState)

// DEV-493 — obras reprovadas saem da listagem de avaliação, então não
// fazem sentido como filtro aqui também. Manter só os estados ativos.
const exhibitionStatusOptions = [
  { value: 'pending', label: 'Pendente' },
  { value: 'approved', label: 'Aprovada' },
  { value: 'stand_by', label: 'Stand-by' }
]

const evaluationStatusOptions = [
  { value: 'evaluated', label: 'Avaliadas' },
  { value: 'pending', label: 'Pendentes' }
]

const showcaseOptions = [
  { value: 'in', label: 'Na vitrine' },
  { value: 'out', label: 'Fora da vitrine' }
]

const orderByOptions = [
  { value: 'name', label: 'Nome da arte' },
  { value: 'applicant_name', label: 'Autor' },
  { value: 'created_at', label: 'Data de envio' },
  { value: 'grade_average', label: 'Nota média' }
]

const orderDirectionOptions = [
  { value: 'asc', label: 'Crescente (A→Z)' },
  { value: 'desc', label: 'Decrescente (Z→A)' }
]

interface IStatCardProps {
  icon: JSX.Element
  label: string
  value: string
  accentBg: string
  accentColor: string
  delay?: number
}

function StatCard({ icon, label, value, accentBg, accentColor, delay = 0 }: IStatCardProps) {
  return (
    <MotionSectionCard
      variant="tile"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay, ease: [0.22, 0.61, 0.36, 1] }}
      position="relative"
      overflow="hidden"
      sx={{
        transition: 'all .2s ease',
        ':hover': { boxShadow: '0 8px 24px -8px rgba(17,24,39,0.1)', transform: 'translateY(-2px)' }
      }}
    >
      <Flex align="center" justify="space-between" mb="3">
        <Flex
          align="center"
          justify="center"
          w="38px"
          h="38px"
          borderRadius="10px"
          bg={accentBg}
          color={accentColor}
          fontSize="18px"
        >
          {icon}
        </Flex>
      </Flex>
      <Text
        fontFamily="Inter, sans-serif"
        fontSize="12px"
        fontWeight="500"
        color="gray.500"
        mb="1"
        letterSpacing="0.01em"
      >
        {label}
      </Text>
      <Text
        fontFamily="Inter, sans-serif"
        fontSize="28px"
        fontWeight="700"
        color="gray.900"
        lineHeight="1.1"
        letterSpacing="-0.02em"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Text>
    </MotionSectionCard>
  )
}

export default function ArtworkEvaluationPage() {
  const contestsService = useMemo(() => new ContestsService(), [])
  const evaluatorsService = useMemo(() => new ContestEvaluatorsService(), [])
  const criteriaService = useMemo(() => new ContestEvaluationCriteriaService(), [])
  const navigate = useNavigate()
  const { slug } = useParams<{ slug?: string }>()
  const toast = useAppToast()
  const hasRole = useHasRole()
  // view_contest_votes opens the page read-only: grades, averages and downloads, no writes.
  const readOnly = !hasRole('evaluate_contest_artworks')

  const [contests, setContests] = useState<IContestForSelect[]>([])
  const [selectedContestId, setSelectedContestId] = useState<string>('')
  const [selectedContestName, setSelectedContestName] = useState<string>('')
  // Lets a promise in flight tell whether the user has since switched to another contest.
  const selectedContestIdRef = useRef(selectedContestId)
  useEffect(() => {
    selectedContestIdRef.current = selectedContestId
  }, [selectedContestId])
  // Contest id alone isn't enough to spot a stale response: leaving and re-selecting the SAME
  // contest before the first attempt settles would make it look current again. A generation
  // counter, bumped on every loadContestSetup() call, is what actually identifies "the latest attempt".
  const setupRequestIdRef = useRef(0)

  const [evaluators, setEvaluators] = useState<IContestEvaluator[]>([])
  const [isManageModalOpen, setIsManageModalOpen] = useState(false)
  const [manageModalRequired, setManageModalRequired] = useState(false)
  const [hasLoadedEvaluators, setHasLoadedEvaluators] = useState(false)

  const [criteria, setCriteria] = useState<IContestEvaluationCriterion[]>([])
  const [isCriteriaModalOpen, setIsCriteriaModalOpen] = useState(false)
  const [criteriaModalRequired, setCriteriaModalRequired] = useState(false)
  const [hasLoadedCriteria, setHasLoadedCriteria] = useState(false)
  // A failed request leaves both lists empty, which must not read as "contest not set up".
  const [loadError, setLoadError] = useState(false)

  const [artworks, setArtworks] = useState<IContestArtworkForEvaluation[]>([])
  const [applicants, setApplicants] = useState<IApplicantSelectOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [artworksError, setArtworksError] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [evaluatedCount, setEvaluatedCount] = useState(0)
  const [pendingCount, setPendingCount] = useState(0)
  const [showcaseCount, setShowcaseCount] = useState(0)
  // Com o concurso fora da galeria, a vitrine curada aqui não sai no site —
  // a tela precisa poder dizer isso em vez de deixar o time no escuro.
  const [contestShowsInGallery, setContestShowsInGallery] = useState(true)
  // Os slots já tomados, no concurso inteiro — a página só enxerga 12 obras por
  // vez, então esta lista não pode sair das obras carregadas.
  const [takenPositions, setTakenPositions] = useState<IShowcaseTakenPosition[]>([])
  // Quantos slots a vitrine oferece. Vem da API junto com a listagem para não
  // divergir do `max:` que o endpoint de curadoria valida.
  const [maxShowcasePosition, setMaxShowcasePosition] = useState(DEFAULT_MAX_SHOWCASE_POSITION)

  const [filter, setFilter] = useState<IArtworkEvaluationFilter>({
    contest_id: '',
    applicant_id: '',
    search: '',
    exhibition_status: '',
    evaluation_status: '',
    showcase: '',
    order_by: 'created_at',
    order_direction: 'desc',
    page: 1,
    page_size: 12
  })

  const [selectedArtwork, setSelectedArtwork] = useState<IContestArtworkForEvaluation | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    contestsService.getAllForSelect().then(setContests)
  }, [contestsService])

  useEffect(() => {
    if (!slug) {
      setSelectedContestId('')
      setSelectedContestName('')
      return
    }
    if (contests.length === 0) return

    const match = contests.find((c) => c.slug === slug)
    if (match) {
      setSelectedContestId(match.id.toString())
      setSelectedContestName(match.name)
    } else {
      navigate('/contests/artwork-evaluation', { replace: true })
    }
  }, [slug, contests, navigate])

  const loadContestSetup = useCallback(() => {
    if (!selectedContestId) return
    // Snapshot: a stale response (after the user switched contests, or after a retry
    // superseded this same attempt) must not touch state anymore.
    const requestContestId = selectedContestId
    const requestId = ++setupRequestIdRef.current
    const isStale = () => requestContestId !== selectedContestIdRef.current || requestId !== setupRequestIdRef.current

    setHasLoadedEvaluators(false)
    setHasLoadedCriteria(false)
    setLoadError(false)

    evaluatorsService
      .list(requestContestId)
      .then((result) => {
        if (isStale()) return
        setEvaluators(result.data)
      })
      .catch((error) => {
        if (isStale()) return
        setLoadError(true)
        toast.error('Erro ao carregar avaliadores do concurso', error)
      })
      .finally(() => {
        if (isStale()) return
        setHasLoadedEvaluators(true)
      })

    criteriaService
      .list(requestContestId)
      .then((result) => {
        if (isStale()) return
        setCriteria(result.data)
      })
      .catch((error) => {
        if (isStale()) return
        setLoadError(true)
        toast.error('Erro ao carregar critérios do concurso', error)
      })
      .finally(() => {
        if (isStale()) return
        setHasLoadedCriteria(true)
      })
  }, [selectedContestId, evaluatorsService, criteriaService, toast])

  useEffect(() => {
    if (!selectedContestId) {
      setEvaluators([])
      setHasLoadedEvaluators(false)
      setCriteria([])
      setHasLoadedCriteria(false)
      setLoadError(false)
      return
    }

    loadContestSetup()
  }, [selectedContestId, loadContestSetup])

  // DEV / ABRA-74 — force registration of evaluators first, then criteria,
  // when either is missing for the selected contest.
  useEffect(() => {
    if (!selectedContestId || !hasLoadedEvaluators || !hasLoadedCriteria) return
    if (readOnly || loadError || isManageModalOpen || isCriteriaModalOpen) return

    if (evaluators.length === 0) {
      setManageModalRequired(true)
      setIsManageModalOpen(true)
    } else if (criteria.length === 0) {
      setCriteriaModalRequired(true)
      setIsCriteriaModalOpen(true)
    }
  }, [
    selectedContestId,
    hasLoadedEvaluators,
    hasLoadedCriteria,
    evaluators.length,
    criteria.length,
    isManageModalOpen,
    isCriteriaModalOpen,
    readOnly,
    loadError
  ])

  useEffect(() => {
    if (!selectedContestId) return
    contestsService.listApplicantsForSelect(selectedContestId).then(setApplicants)
  }, [contestsService, selectedContestId])

  useEffect(() => {
    if (!selectedContestId || evaluators.length === 0 || criteria.length === 0) return

    const timeoutId = setTimeout(() => {
      fetchArtworks()
    }, 500)
    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selectedContestId, evaluators.length, criteria.length])

  const fetchArtworks = async () => {
    setIsLoading(true)
    setArtworksError(false)
    try {
      const result = await contestsService.listArtworksForEvaluation({
        ...filter,
        contest_id: selectedContestId
      })
      setArtworks(result.data)
      setTotalPages(Math.ceil(result.total_filtered / filter.page_size) || 1)
      setPage(result.current_page)
      setTotalCount(result.total_filtered)
      setEvaluatedCount(result.evaluated_count)
      setPendingCount(result.pending_count)
      setShowcaseCount(result.showcase_count)
      setContestShowsInGallery(result.contest_shows_in_gallery)
      setTakenPositions(result.showcase_taken_positions)
      setMaxShowcasePosition(result.showcase_max_position ?? DEFAULT_MAX_SHOWCASE_POSITION)
    } catch (error) {
      setArtworksError(true)
      setArtworks([])
      setTotalPages(1)
      setTotalCount(0)
      setEvaluatedCount(0)
      setPendingCount(0)
      setShowcaseCount(0)
      setTakenPositions([])
      toast.error('Erro ao carregar as obras', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleContestSelect = (contestId: string) => {
    const contest = contests.find((c) => c.id.toString() === contestId)
    if (!contest) return
    setFilter({
      contest_id: contestId,
      applicant_id: '',
      search: '',
      exhibition_status: '',
      evaluation_status: '',
      showcase: '',
      order_by: 'created_at',
      order_direction: 'desc',
      page: 1,
      page_size: 12
    })
    navigate(`/contests/artwork-evaluation/${contest.slug}`)
  }

  const handleSwitchContest = () => {
    setEvaluators([])
    setCriteria([])
    setArtworks([])
    setHasLoadedEvaluators(false)
    setHasLoadedCriteria(false)
    navigate('/contests/artwork-evaluation')
  }

  const handleOpenManageEvaluators = () => {
    setManageModalRequired(false)
    setIsManageModalOpen(true)
  }

  const handleOpenManageCriteria = () => {
    setCriteriaModalRequired(false)
    setIsCriteriaModalOpen(true)
  }

  const [isExporting, setIsExporting] = useState(false)
  const handleExportXlsx = async () => {
    if (!selectedContestId) return
    const slug = contests.find((c) => c.id.toString() === selectedContestId)?.slug || `contest-${selectedContestId}`
    setIsExporting(true)
    try {
      await contestsService.downloadArtworkEvaluationsXlsx(selectedContestId, slug)
    } catch (error) {
      toast.error('Erro ao exportar avaliações', error)
    } finally {
      setIsExporting(false)
    }
  }

  // ABRA-297 — o pessoal do ABRA baixa as imagens das obras para publicar no site.
  // O ZIP leva exatamente as obras do filtro atual, sem a paginação.
  const [isDownloadingImages, setIsDownloadingImages] = useState(false)
  const handleDownloadImages = async () => {
    if (!selectedContestId) return
    const contestSlug =
      contests.find((c) => c.id.toString() === selectedContestId)?.slug || `contest-${selectedContestId}`
    setIsDownloadingImages(true)
    try {
      await contestsService.downloadArtworkImagesZip({ ...filter, contest_id: selectedContestId }, contestSlug)
      toast.success('Imagens baixadas')
    } catch (error) {
      toast.error('Erro ao baixar as imagens das obras', error)
    } finally {
      setIsDownloadingImages(false)
    }
  }

  const handleEvaluatorsSaved = (saved: IContestEvaluator[]) => {
    setEvaluators(saved)
  }

  const handleCriteriaSaved = (saved: IContestEvaluationCriterion[]) => {
    setCriteria(saved)
  }

  const handleEdit = (artwork: IContestArtworkForEvaluation) => {
    setSelectedArtwork(artwork)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedArtwork(null)
  }

  const handleEvaluationSaved = (artId: number, updated: IContestArtworkForEvaluation) => {
    setArtworks((prev) => prev.map((a) => (a.id === artId ? updated : a)))
    setSelectedArtwork(updated)
  }

  /**
   * ABRA-314 — a curadoria da vitrine já voltou confirmada da API, então a obra
   * é repintada no lugar em vez de refazer a busca: um refetch por clique de
   * switch perderia a rolagem da grade no meio da curadoria.
   */
  const handleShowcaseChanged = (artId: number, state: IArtworkShowcaseState, wasSelected: boolean) => {
    setArtworks((prev) =>
      prev.map((artwork) =>
        artwork.id === artId
          ? {
              ...artwork,
              showcase_override: state.showcase_override,
              showcase_position: state.showcase_position,
              showcase_auto: state.showcase_auto,
              showcase_selected: state.showcase_selected
            }
          : artwork
      )
    )
    setTakenPositions(state.taken_positions)
    // `wasSelected` vem do card, que é quem tem a obra em mãos no momento do
    // clique. Ler `artworks` daqui seria ler a closure do render, que pode
    // estar uma gravação atrás quando duas obras são curadas em sequência.
    if (wasSelected !== state.showcase_selected) {
      setShowcaseCount((previous) => Math.max(0, previous + (state.showcase_selected ? 1 : -1)))
    }
  }

  const handleStatusChanged = () => {
    // Reload artworks to sync the updated status from the backend
    fetchArtworks()
  }

  // DEV-489 — prev/next navigation across the loaded artworks list.
  const currentArtworkIndex = useMemo(() => {
    if (!selectedArtwork) return -1
    return artworks.findIndex((a) => a.id === selectedArtwork.id)
  }, [artworks, selectedArtwork])

  const handlePrevArtwork = useMemo(() => {
    if (currentArtworkIndex <= 0) return undefined
    return () => setSelectedArtwork(artworks[currentArtworkIndex - 1])
  }, [artworks, currentArtworkIndex])

  const handleNextArtwork = useMemo(() => {
    if (currentArtworkIndex < 0 || currentArtworkIndex >= artworks.length - 1) return undefined
    return () => setSelectedArtwork(artworks[currentArtworkIndex + 1])
  }, [artworks, currentArtworkIndex])

  const positionLabel = useMemo(() => {
    if (currentArtworkIndex < 0 || artworks.length === 0) return undefined
    return `Obra ${currentArtworkIndex + 1} de ${artworks.length}`
  }, [artworks.length, currentArtworkIndex])

  const handleApplicantChange = (applicantId: string) => {
    setFilter((prev) => ({ ...prev, applicant_id: applicantId, page: 1 }))
  }

  const hasNoResults = !isLoading && !artworksError && artworks.length === 0
  const showGate = !selectedContestId
  // ABRA-74 — both evaluators and criteria are required before evaluating.
  const isReadyToEvaluate = hasLoadedEvaluators && hasLoadedCriteria && evaluators.length > 0 && criteria.length > 0
  const hasLoadedSetup = hasLoadedEvaluators && hasLoadedCriteria
  const showLoadError = hasLoadedSetup && loadError
  // Between picking a contest and both lists finishing, there was only the header — a blank screen.
  const isLoadingSetup = !!selectedContestId && !hasLoadedSetup && !showLoadError
  // A viewer cannot fix a contest that is not set up yet, so tell them instead of forcing the modals.
  const showSetupNotice = readOnly && hasLoadedSetup && !loadError && !isReadyToEvaluate

  return (
    // A `Inter` é a tipografia própria deste módulo, na mesma exceção que o `react-icons` dele
    // tem no eixo de ícones: sai quando o módulo inteiro sair.
    <Box fontFamily="Inter, sans-serif" color="gray.900">
      {showGate ? (
        <ContestSelectionGate contests={contests} onSelect={handleContestSelect} readOnly={readOnly} />
      ) : (
        <>
          {/* HEADER */}
          <MotionBox
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
            mb="6"
          >
            <PageTitle
              title="Avaliação de Obras"
              mb="0"
              description={
                <>
                  Concurso:{' '}
                  <Text as="span" fontWeight="600" color="gray.700">
                    {selectedContestName}
                  </Text>
                  {' · '}
                  {evaluators.length} avaliador{evaluators.length === 1 ? '' : 'es'}
                  {' · '}
                  {criteria.length} critério{criteria.length === 1 ? '' : 's'}
                  {readOnly && ' · somente leitura'}
                </>
              }
              actions={
                <>
                  <ActionButton
                    action="download"
                    label="Exportar Excel"
                    showLabel
                    mr={0}
                    isLoading={isExporting}
                    loadingText="Exportando..."
                    onClick={handleExportXlsx}
                  />
                  <Tooltip
                    label={
                      totalCount === 0
                        ? 'Nenhuma obra no filtro atual'
                        : `Baixa um ZIP com as imagens das ${totalCount} obra${totalCount === 1 ? '' : 's'} do filtro atual`
                    }
                    placement="bottom"
                  >
                    {/* Botão desabilitado não dispara eventos de mouse: o span garante o tooltip,
                        que é justamente onde a explicação do "sem obras no filtro" aparece. O
                        `hideTooltip` desliga o do próprio ActionButton, que só repetiria o rótulo. */}
                    <Box as="span" display="inline-block">
                      <ActionButton
                        action="download"
                        icon={<i className="lni lni-image" />}
                        label="Baixar imagens"
                        showLabel
                        hideTooltip
                        mr={0}
                        isLoading={isDownloadingImages}
                        loadingText="Baixando..."
                        isDisabled={totalCount === 0}
                        onClick={handleDownloadImages}
                      />
                    </Box>
                  </Tooltip>
                  {!readOnly && (
                    <>
                      <ActionButton
                        action="settings"
                        icon={<i className="lni lni-users" />}
                        label="Gerenciar avaliadores"
                        showLabel
                        mr={0}
                        onClick={handleOpenManageEvaluators}
                      />
                      <ActionButton
                        action="settings"
                        label="Gerenciar critérios"
                        showLabel
                        mr={0}
                        onClick={handleOpenManageCriteria}
                      />
                    </>
                  )}
                  <Button size="sm" variant="ghost" colorScheme="gray" onClick={handleSwitchContest}>
                    Trocar concurso
                  </Button>
                </>
              }
            />
          </MotionBox>

          {/* LOADING SETUP */}
          {isLoadingSetup && <LoadingBlock size="page" entity="avaliação" />}

          {/* LOAD ERROR */}
          {showLoadError && (
            <MotionEmptyState
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              variant="panel"
              tone="error"
              icon={<FiAlertCircle />}
              title="Não foi possível carregar a avaliação"
              description="Falha ao buscar avaliadores ou critérios deste concurso. Verifique a conexão e tente novamente."
              action={<ActionButton action="refresh" showLabel label="Tentar novamente" onClick={loadContestSetup} />}
            />
          )}

          {/* SETUP NOTICE (read-only) */}
          {showSetupNotice && (
            <MotionEmptyState
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              variant="panel"
              icon={evaluators.length === 0 ? <FiUsers /> : <FiSliders />}
              title="Concurso ainda sem avaliação"
              description={
                <>
                  Este concurso ainda não tem{' '}
                  {evaluators.length === 0 && criteria.length === 0
                    ? 'avaliadores nem critérios'
                    : evaluators.length === 0
                      ? 'avaliadores'
                      : 'critérios'}{' '}
                  cadastrados. As notas aparecem aqui assim que a avaliação for configurada.
                </>
              }
            />
          )}

          {/* STATS */}
          {isReadyToEvaluate && (
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="4" mb="6">
              <StatCard
                icon={<FiImage />}
                label="Total de obras"
                value={String(totalCount).padStart(2, '0')}
                accentBg="gray.100"
                accentColor="gray.700"
                delay={0.04}
              />
              <StatCard
                icon={<FiCheckCircle />}
                label="Avaliadas"
                value={String(evaluatedCount).padStart(2, '0')}
                accentBg="magenta.50"
                accentColor="magenta.500"
                delay={0.08}
              />
              <StatCard
                icon={<FiClock />}
                label="Pendentes"
                value={String(pendingCount).padStart(2, '0')}
                accentBg="orange.50"
                accentColor="orange.500"
                delay={0.12}
              />
              <StatCard
                icon={<FiStar />}
                label={contestShowsInGallery ? 'Na vitrine do site' : 'Na vitrine (concurso oculto)'}
                value={String(showcaseCount).padStart(2, '0')}
                accentBg={contestShowsInGallery ? 'pink.50' : 'gray.100'}
                accentColor={contestShowsInGallery ? 'pink.500' : 'gray.500'}
                delay={0.16}
              />
            </SimpleGrid>
          )}

          {/* FILTERS */}
          {isReadyToEvaluate && (
            <MotionSectionCard
              variant="tile"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.2 }}
              mb="6"
            >
              <Flex flexWrap="wrap" gap="4">
                <FormField label="Artista" minW="200px" flex="1">
                  <SearchableSelect
                    isClearable
                    placeholder="Todos os artistas"
                    entity="artista"
                    options={applicants.map((applicant) => ({ value: applicant.id.toString(), label: applicant.name }))}
                    value={filter.applicant_id}
                    onChange={handleApplicantChange}
                  />
                </FormField>

                <FormField label="Status da exposição" minW="180px" flex="1">
                  <SearchableSelect
                    isClearable
                    placeholder="Todos"
                    options={exhibitionStatusOptions}
                    value={filter.exhibition_status}
                    onChange={(value) => setFilter((prev) => ({ ...prev, exhibition_status: value, page: 1 }))}
                  />
                </FormField>

                <FormField label="Pesquisa" minW="220px" flex="1.2">
                  <Box position="relative">
                    <Box
                      position="absolute"
                      left="12px"
                      top="50%"
                      transform="translateY(-50%)"
                      color="gray.400"
                      pointerEvents="none"
                      fontSize="16px"
                      display="flex"
                      alignItems="center"
                      zIndex="1"
                    >
                      <FiSearch />
                    </Box>
                    <Input
                      type="search"
                      placeholder="Nome da obra ou artista"
                      value={filter.search}
                      onChange={(e) => setFilter((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
                      borderRadius="10px"
                      borderColor="gray.200"
                      fontSize="14px"
                      pl="38px"
                      h="42px"
                      bg="white"
                      _placeholder={{ color: 'gray.400' }}
                      _hover={{ borderColor: 'gray.300' }}
                      _focus={{ borderColor: 'magenta.500', boxShadow: '0 0 0 3px rgba(250,0,105,0.12)' }}
                    />
                  </Box>
                </FormField>

                <FormField label="Avaliação" minW="160px" flex="1">
                  <SearchableSelect
                    isClearable
                    placeholder="Todas"
                    options={evaluationStatusOptions}
                    value={filter.evaluation_status}
                    onChange={(value) => setFilter((prev) => ({ ...prev, evaluation_status: value, page: 1 }))}
                  />
                </FormField>

                <FormField label="Vitrine do site" minW="160px" flex="1">
                  <SearchableSelect
                    isClearable
                    placeholder="Todas"
                    options={showcaseOptions}
                    value={filter.showcase}
                    onChange={(value) => setFilter((prev) => ({ ...prev, showcase: value, page: 1 }))}
                  />
                </FormField>

                <Flex align="flex-end" flexShrink={0}>
                  <Popover placement="bottom-end">
                    <PopoverTrigger>
                      <IconButton
                        aria-label="Ordenar resultados"
                        icon={<FiSliders />}
                        borderRadius="full"
                        h="42px"
                        w="42px"
                        variant="outline"
                        borderColor={
                          filter.order_by !== 'created_at' || filter.order_direction !== 'desc'
                            ? 'magenta.500'
                            : 'gray.200'
                        }
                        color={
                          filter.order_by !== 'created_at' || filter.order_direction !== 'desc'
                            ? 'magenta.500'
                            : 'gray.500'
                        }
                        _hover={{ borderColor: 'magenta.500', color: 'magenta.500' }}
                      />
                    </PopoverTrigger>
                    <PopoverContent
                      w="260px"
                      borderRadius="12px"
                      boxShadow="0 10px 38px -10px rgba(22,23,24,0.18)"
                      border="1px solid"
                      borderColor="gray.200"
                      zIndex="popover"
                    >
                      <PopoverArrow />
                      <PopoverBody p="4">
                        <FormField label="Ordenar por" mb="3">
                          <SearchableSelect
                            options={orderByOptions}
                            value={filter.order_by}
                            onChange={(orderBy) =>
                              setFilter((prev) => ({ ...prev, order_by: orderBy || 'created_at', page: 1 }))
                            }
                          />
                        </FormField>
                        <FormField label="Direção">
                          <SearchableSelect
                            options={orderDirectionOptions}
                            value={filter.order_direction}
                            onChange={(direction) =>
                              setFilter((prev) => ({ ...prev, order_direction: direction || 'desc', page: 1 }))
                            }
                          />
                        </FormField>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                </Flex>
              </Flex>
            </MotionSectionCard>
          )}

          {/* LOAD ERROR (grade) */}
          {isReadyToEvaluate && !isLoading && artworksError && (
            <MotionEmptyState
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              variant="panel"
              tone="error"
              icon={<FiAlertCircle />}
              title="Não foi possível carregar as obras"
              action={<ActionButton action="refresh" showLabel label="Tentar novamente" onClick={fetchArtworks} />}
            />
          )}

          {/* EMPTY STATE */}
          {isReadyToEvaluate && hasNoResults && (
            <MotionEmptyState
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              variant="panel"
              icon={<FiImage />}
              title="Nenhuma obra encontrada"
              description="Ajuste os filtros para encontrar as obras que você quer avaliar."
            />
          )}

          {/* GRID */}
          {isReadyToEvaluate && (
            <MotionBox animate={{ opacity: isLoading ? 0.5 : 1 }} transition={{ duration: 0.2 }}>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="5" mb="6">
                {artworks.map((artwork, index) => (
                  <ArtworkEvaluationCard
                    key={artwork.id}
                    artwork={artwork}
                    criteria={criteria}
                    index={index}
                    onEdit={handleEdit}
                    takenPositions={takenPositions}
                    maxShowcasePosition={maxShowcasePosition}
                    onShowcaseChanged={handleShowcaseChanged}
                    readOnly={readOnly}
                  />
                ))}
              </SimpleGrid>
            </MotionBox>
          )}

          {totalPages > 1 && (
            <Paginator
              page={page}
              totalPages={totalPages}
              changePage={(newPage) => setFilter((prev) => ({ ...prev, page: newPage }))}
            />
          )}

          <ArtworkEvaluationModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            artwork={selectedArtwork}
            evaluators={evaluators}
            criteria={criteria}
            onSaved={handleEvaluationSaved}
            onPrev={handlePrevArtwork}
            onNext={handleNextArtwork}
            positionLabel={positionLabel}
            onStatusChanged={handleStatusChanged}
            readOnly={readOnly}
          />

          <EvaluatorsManagementModal
            isOpen={isManageModalOpen}
            onClose={() => setIsManageModalOpen(false)}
            contestId={selectedContestId}
            contestName={selectedContestName}
            initialEvaluators={evaluators}
            required={manageModalRequired}
            onSaved={handleEvaluatorsSaved}
          />

          <CriteriaManagementModal
            isOpen={isCriteriaModalOpen}
            onClose={() => setIsCriteriaModalOpen(false)}
            contestId={selectedContestId}
            contestName={selectedContestName}
            initialCriteria={criteria}
            required={criteriaModalRequired}
            onSaved={handleCriteriaSaved}
          />
        </>
      )}
    </Box>
  )
}
